import { Component, OnInit, ViewEncapsulation, ViewChild, ChangeDetectorRef, DestroyRef, Input, inject } from "@angular/core";
import { NgComponentOutlet } from "@angular/common";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { CustomFieldBase } from "../custom-field-base";
import { GenericListConfig } from "@app/shared/models/GenericList/GenericList";
import { GenericListFieldType } from "@app/shared/models/GenericList/GenericListField";
import type { GenericListManagerComponent } from "@app/shared/generic-managers/generic-list-manager/generic-list-manager.component";
import { GENERIC_LIST_MANAGER } from "@app/shared/generic-managers/generic-list-manager/generic-list-manager.token";
import { ToastrService } from "ngx-toastr";
import { of } from "rxjs";
import { MatrixModel } from "@models/generic/MatrixModel";
import { TranslocoPipe, TranslocoService, provideTranslocoScope } from "@jsverse/transloco";
import { CheckboxComponent } from "@app/shared/cerrix-checkbox/cerrix-checkbox.component";
import { CerrixRiskmatrixSummaryComponent } from "@app/shared/components/cerrix-riskmatrix-summary/cerrix-riskmatrix-summary.component";

const ORGANIZATION_RISKMATRIX_EDITOR_SCOPE =
    "shared/generic-managers/generic-field-editor/custom-field-types/organization-riskmatrix-editor";

@Component({
    selector: "organization-riskmatrix-editor",
    templateUrl: "./organization-riskmatrix-editor.component.html",
    styleUrls: ["./organization-riskmatrix-editor.component.scss"],
    encapsulation: ViewEncapsulation.None,
    imports: [TranslocoPipe, CheckboxComponent, CerrixRiskmatrixSummaryComponent, NgComponentOutlet],
    providers: [
        provideTranslocoScope({
            scope: ORGANIZATION_RISKMATRIX_EDITOR_SCOPE,
            alias: "organizationRiskmatrixEditor",
        }),
    ],
})
export class OrganizationRiskmatrixEditorComponent extends CustomFieldBase implements OnInit {
    private cd = inject(ChangeDetectorRef);
    private toastr = inject(ToastrService);
    private transloco = inject(TranslocoService);
    private destroyRef = inject(DestroyRef);

    @Input() row: any;

    protected readonly listManagerType = inject(GENERIC_LIST_MANAGER);
    @ViewChild(NgComponentOutlet) private summaryManagerOutlet: NgComponentOutlet;

    get summaryManager(): GenericListManagerComponent | undefined {
        return this.summaryManagerOutlet?.componentInstance;
    }

    cellSize = 60;
    cellSpacing = 2;

    summaryListConfig: GenericListConfig;

    ngOnInit() {
        if (!this.value) {
            this.value = <MatrixModel>{
                likelihoodSize: 5,
                impactSize: 5,
                colors: [],
                matrix: {},
            };
        }

        this.fixConfig();

        // cerrix-table copies field.prettyName into a private map once, from its own ngOnInit,
        // so the column header has to be a real string before the child renders. Load the scope
        // first and build the config after; `@if (summaryListConfig)` holds the template until then.
        const scopeLang = `${ORGANIZATION_RISKMATRIX_EDITOR_SCOPE}/${this.transloco.getActiveLang()}`;
        this.transloco
            .load(scopeLang)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.initSummaryListConfig(scopeLang));
    }

    private initSummaryListConfig(scopeLang: string) {
        this.summaryListConfig = {
            allowAdd: true,
            allowEdit: true,
            allowDelete: true,

            hideRefreshButton: true,

            isSortable: true,

            fields: [
                {
                    prettyName: this.transloco.translate("colorColumn", {}, scopeLang),
                    fieldName: "Color",
                    fieldType: GenericListFieldType.ColorPicker,
                    unique: true,
                    required: true,
                    defaultValue: "#000000",
                },
            ],

            dataMethod: () => {
                return of(this.value.colors.map((color, i) => ({ ID: color, Color: color })));
            },

            onSortChange: (sortedData) => {
                this.value.colors = sortedData.map((x) => x.Color);
                this.emitChange();
            },

            add: async (row) => {
                this.value.colors.push(row.Color);
                this.emitChange();
                return true;
            },

            edit: async (row, uneditedRow) => {
                const index = this.value.colors.indexOf(uneditedRow.Color);
                this.value.colors[index] = row.Color;
                this.emitChange();

                setTimeout(() => {
                    this.summaryManager.activeRow = this.summaryManager.tableData.find(
                        (x) => x.Color === row.Color,
                    );
                }, 0);

                return true;
            },

            preDeleteCheck: async () => {
                const allowed = this.value.colors.length > 1;
                if (!allowed) {
                    // Read against the scope-qualified lang, the way workspace-advanced-config
                    // does. generic-list-manager only calls this from its delete button, so the
                    // template pipes above have already loaded the scope by then.
                    const scopeLang = `${ORGANIZATION_RISKMATRIX_EDITOR_SCOPE}/${this.transloco.getActiveLang()}`;
                    this.toastr.warning(
                        this.transloco.translate("cannotRemoveColorMessage", {}, scopeLang),
                        this.transloco.translate("cannotRemoveColorTitle", {}, scopeLang),
                    );
                }
                return allowed;
            },

            delete: async (row) => {
                const index = this.value.colors.indexOf(row.Color);
                this.value.colors.splice(index, 1);
                this.emitChange();

                return true;
            },
        };
    }

    /**
     * generic-list-manager renders `config.name`, but the name is only known once the
     * co-located scope is loaded. The template feeds the piped value in, so the transloco
     * pipe loads the scope under its alias instead of an imperative load() (which would
     * register the scope under its camelCase path and make every pipe here miss).
     */
    withSummaryListName(name: string): GenericListConfig {
        this.summaryListConfig.name = name;
        return this.summaryListConfig;
    }

    fixConfig() {
        if (this.value.colors.length === 0) {
            this.value.colors.push("#008f05");
        }

        if (!this.value.matrix) {
            this.value.matrix = {};
        }

        for (let yIndex = 0; yIndex < this.value.likelihoodSize; yIndex++) {
            if (!this.value.matrix[yIndex]) {
                this.value.matrix[yIndex] = [];
            }

            for (let xIndex = 0; xIndex < this.value.impactSize; xIndex++) {
                const colorPointer = this.value.matrix[yIndex][xIndex];
                if (!+colorPointer) {
                    this.value.matrix[yIndex][xIndex] = 0;
                }
            }
        }
    }

    matrixCellClick(y: number, x: number) {
        if (!this.summaryManager || !this.summaryManager.activeRow) {
            return;
        }
        this.value.matrix[y][x] = this.value.colors.indexOf(this.summaryManager.activeRow.Color);
        this.emitChange();
    }

    emitChange() {
        this.valueChange.emit(this.value);
        this.cd.detectChanges();
    }
}
